/**
 * Secret Santa draws: a random assignment in which everyone gives to exactly one person,
 * everyone receives from exactly one person, and nobody draws themselves — a
 * derangement — that also respects a list of people who must not draw each other.
 *
 * ## Method
 *
 * 1. Build the table of who may give to whom (not yourself, not an excluded pair).
 * 2. Check that any valid assignment exists at all, by finding a maximum bipartite
 *    matching. If one is missing, the unmatched giver's alternating tree yields a
 *    Hall violation: a set of givers who between them have fewer allowed receivers than
 *    there are givers. That set is named in the message, so the organiser can see which
 *    exclusions to relax.
 * 3. Shuffle everyone (Fisher–Yates over `crypto.getRandomValues`) and test the shuffle.
 *    Repeating until a shuffle passes picks one of the valid assignments with equal
 *    probability. Without exclusions about 37% (1/e) of shuffles pass, so it takes
 *    under three tries on average.
 * 4. If a few hundred shuffles all fail — heavy exclusions — fall back to a randomised
 *    depth-first search. It always finds an assignment when one exists, but it favours
 *    some valid assignments over others, and the result says so.
 *
 * The "no swaps" option also forbids A→B together with B→A. That condition is not captured
 * by the matching in step 2, so the search is exhaustive (within a node limit) and the
 * result distinguishes "proved impossible" from "gave up".
 */

import { shuffle, type Uint32Source } from "@/lib/tool-kit/random-fun/rng";

export const MAX_PEOPLE = 100;
const SHUFFLE_ATTEMPTS = 400;
const SEARCH_NODE_LIMIT = 200_000;
const LINE_BREAK = /\r?\n/;
const PAIR_SEPARATOR = /\s*(?:,|&|;)\s*/;
const ONE_WAY_SEPARATOR = /\s*(?:->|→|>)\s*/;

/* ------------------------------------------------------------------ parsing */

export type NameProblem =
  | { kind: "tooFew" }
  | { kind: "tooMany" }
  | { kind: "duplicate"; name: string };

/** The names, one per line, with blanks removed. Duplicates (ignoring case) are a problem. */
export function parseNames(
  text: string
): { names: string[] } | { problem: NameProblem } {
  const names = text
    .split(LINE_BREAK)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  const seen = new Set<string>();
  for (const name of names) {
    const key = name.toLowerCase();
    if (seen.has(key)) {
      return { problem: { kind: "duplicate", name } };
    }
    seen.add(key);
  }
  if (names.length < 2) {
    return { problem: { kind: "tooFew" } };
  }
  if (names.length > MAX_PEOPLE) {
    return { problem: { kind: "tooMany" } };
  }
  return { names };
}

export interface Exclusion {
  /** Index of the giver who may not give to `to`. */
  from: number;
  to: number;
}

export type ExclusionProblem =
  | { kind: "unknown"; name: string; line: string }
  | { kind: "unreadable"; line: string };

/**
 * Exclusion lines. `Ana, Ben` (or `Ana & Ben`) means neither may draw the other;
 * `Ana > Ben` means only that Ana may not draw Ben. Names match ignoring case.
 */
export function parseExclusions(
  text: string,
  names: readonly string[]
): { exclusions: Exclusion[] } | { problem: ExclusionProblem } {
  const index = new Map(names.map((name, i) => [name.toLowerCase(), i]));
  const exclusions: Exclusion[] = [];
  for (const raw of text.split(LINE_BREAK)) {
    const line = raw.trim();
    if (line.length === 0) {
      continue;
    }
    const oneWay = ONE_WAY_SEPARATOR.test(line);
    const parts = line
      .split(oneWay ? ONE_WAY_SEPARATOR : PAIR_SEPARATOR)
      .map((part) => part.trim());
    if (parts.length !== 2 || !parts[0] || !parts[1]) {
      return { problem: { kind: "unreadable", line } };
    }
    const from = index.get(parts[0].toLowerCase());
    const to = index.get(parts[1].toLowerCase());
    if (from === undefined || to === undefined) {
      const missing = from === undefined ? parts[0] : parts[1];
      return { problem: { kind: "unknown", name: missing, line } };
    }
    exclusions.push({ from, to });
    if (!oneWay) {
      exclusions.push({ from: to, to: from });
    }
  }
  return { exclusions };
}

/** `allowed[giver][receiver]`: true unless it is the giver or an excluded pair. */
export function buildAllowed(
  count: number,
  exclusions: readonly Exclusion[]
): boolean[][] {
  const allowed = Array.from({ length: count }, (_, giver) =>
    Array.from({ length: count }, (_, receiver) => giver !== receiver)
  );
  for (const { from, to } of exclusions) {
    const row = allowed[from];
    if (row) {
      row[to] = false;
    }
  }
  return allowed;
}

/* ------------------------------------------------------- existence (matching) */

export interface HallViolation {
  /** Givers who, between them, have too few allowed receivers. */
  givers: number[];
  /** The only receivers any of those givers may draw. */
  receivers: number[];
}

/**
 * Whether every giver can be given a different allowed receiver. When they cannot, the
 * violation names a set of givers with fewer allowed receivers than members (Hall's
 * marriage theorem), which is the smallest honest explanation.
 */
export function findViolation(
  allowed: readonly (readonly boolean[])[]
): HallViolation | null {
  const n = allowed.length;
  const giverOf: number[] = new Array(n).fill(-1);

  const augment = (giver: number, seen: boolean[]): boolean => {
    for (let r = 0; r < n; r += 1) {
      if (allowed[giver]?.[r] && !seen[r]) {
        seen[r] = true;
        const holder = giverOf[r] as number;
        if (holder === -1 || augment(holder, seen)) {
          giverOf[r] = giver;
          return true;
        }
      }
    }
    return false;
  };

  for (let giver = 0; giver < n; giver += 1) {
    const seen: boolean[] = new Array(n).fill(false);
    if (!augment(giver, seen)) {
      // `seen` now holds exactly the receivers reachable from this giver by alternating
      // paths; their holders, plus this giver, are the violating set.
      const receivers = seen.flatMap((hit, r) => (hit ? [r] : []));
      const givers = [giver, ...receivers.map((r) => giverOf[r] as number)];
      return { givers: givers.sort((a, b) => a - b), receivers };
    }
  }
  return null;
}

/* ------------------------------------------------------------------ drawing */

export type DrawResult =
  | {
      kind: "ok";
      /** `receiver[i]` is the index of the person that person `i` gives to. */
      receiver: number[];
      /** True when every valid assignment was equally likely. */
      uniform: boolean;
    }
  | { kind: "impossible"; violation: HallViolation }
  | { kind: "noSwapsImpossible" }
  | { kind: "gaveUp" };

function isValid(
  receiver: readonly number[],
  allowed: readonly (readonly boolean[])[],
  noSwaps: boolean
): boolean {
  for (let giver = 0; giver < receiver.length; giver += 1) {
    const target = receiver[giver] as number;
    if (!allowed[giver]?.[target]) {
      return false;
    }
    if (noSwaps && receiver[target] === giver) {
      return false;
    }
  }
  return true;
}

type SearchOutcome = number[] | "exhausted" | "limit";

function search(
  allowed: readonly (readonly boolean[])[],
  noSwaps: boolean,
  source?: Uint32Source
): SearchOutcome {
  const n = allowed.length;
  const receiver: number[] = new Array(n).fill(-1);
  const taken: boolean[] = new Array(n).fill(false);
  let nodes = 0;
  let limited = false;

  const place = (giver: number): boolean => {
    if (giver === n) {
      return true;
    }
    const options = shuffle(
      Array.from({ length: n }, (_, r) => r).filter(
        (r) =>
          allowed[giver]?.[r] &&
          !taken[r] &&
          !(noSwaps && receiver[r] === giver)
      ),
      source
    );
    for (const choice of options) {
      nodes += 1;
      if (nodes > SEARCH_NODE_LIMIT) {
        limited = true;
        return false;
      }
      receiver[giver] = choice;
      taken[choice] = true;
      if (place(giver + 1)) {
        return true;
      }
      taken[choice] = false;
      receiver[giver] = -1;
      if (limited) {
        return false;
      }
    }
    return false;
  };

  if (place(0)) {
    return receiver;
  }
  return limited ? "limit" : "exhausted";
}

/** A random valid assignment, or the reason there is none. */
export function drawSantas(
  allowed: readonly (readonly boolean[])[],
  noSwaps: boolean,
  source?: Uint32Source
): DrawResult {
  const violation = findViolation(allowed);
  if (violation) {
    return { kind: "impossible", violation };
  }
  const people = Array.from({ length: allowed.length }, (_, i) => i);
  for (let attempt = 0; attempt < SHUFFLE_ATTEMPTS; attempt += 1) {
    const candidate = shuffle(people, source);
    if (isValid(candidate, allowed, noSwaps)) {
      return { kind: "ok", receiver: candidate, uniform: true };
    }
  }
  const found = search(allowed, noSwaps, source);
  if (Array.isArray(found)) {
    return { kind: "ok", receiver: found, uniform: false };
  }
  return found === "exhausted"
    ? { kind: "noSwapsImpossible" }
    : { kind: "gaveUp" };
}
